import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Benefit } from '../@types/benefits';
import { saveBenefitViewedStatus } from '../api/benefits/benefits';
import type { UserInteractionStatus } from '../@types/benefitsViewedStatus';
import { saveLegalInsuranceTipViewedStatus } from '../api/tips/tips';
import { report } from '../tracking/tracking';
import { TrackingEvents } from '../tracking/trackingEvents';
import { useMembership } from './useMembership';
import { getUserInteractions } from '../api/userInteractions/userInteractions';

export const useUserInteractions = (
  enabled: boolean = true,
): {
  numberOfUnviewedBenefits?: number;
  viewedBenefits?: Benefit[];
  showLegalInsuranceHint?: boolean;
  areUserInteractionsLoading: boolean;
  isUserInteractionError?: boolean;
} => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['user-interactions'],
    queryFn: getUserInteractions,
    enabled,
  });

  return {
    numberOfUnviewedBenefits: data?.numberOfUnviewedBenefits,
    viewedBenefits: data?.viewedBenefits,
    showLegalInsuranceHint: data !== undefined && !data?.legalInsuranceHintDismissed,
    areUserInteractionsLoading: isLoading && enabled,
    isUserInteractionError: isError,
  };
};

export const useSelectBenefit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (benefit: Benefit) => saveBenefitViewedStatus(benefit),
    onMutate: async (benefit: Benefit) => {
      await queryClient.cancelQueries({ queryKey: ['user-interactions'] });
      const previousUserInteractions = queryClient.getQueryData<UserInteractionStatus>(['user-interactions']);
      if (
        previousUserInteractions?.viewedBenefits &&
        previousUserInteractions?.numberOfUnviewedBenefits !== undefined
      ) {
        queryClient.setQueryData(['user-interactions'], {
          ...previousUserInteractions,
          numberOfUnviewedBenefits: previousUserInteractions.numberOfUnviewedBenefits - 1,
          viewedBenefits: [...previousUserInteractions.viewedBenefits, benefit],
        });
      }
      return { previousUserInteractions };
    },
  });
};

export const useDismissLegalInsuranceHint = () => {
  const { contractSource } = useMembership();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      saveLegalInsuranceTipViewedStatus().then(() => report(TrackingEvents.INSURANCE_TIP.TIP_CLOSED, !!contractSource)),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['user-interactions'] });
      const previousUserInteractions = queryClient.getQueryData<UserInteractionStatus>(['user-interactions']);
      if (previousUserInteractions) {
        queryClient.setQueryData(['user-interactions'], {
          ...previousUserInteractions,
          legalInsuranceHintDismissed: true,
        });
      }
      return { previousUserInteractions };
    },
  });
};
